import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import CustomFeatured from './CustomFeatured';
import CustomMembership from './CustomMembership';
import css from './SectionColumns.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionColumns = props => {
  const config = useConfiguration();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  // Check if this is the home-featured section that needs custom rendering
  const isHomeFeatured = sectionId === 'home-featured';
  // Check if this is the memberships join header (full replacement with custom component)
  const isMembershipHeader = sectionId === 'memberships-join-header';

  // Hide membership CMS sections entirely when the feature is disabled.
  if (isMembershipHeader && !config.membership?.enabled) {
    return null;
  }

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {isMembershipHeader ? (
        <CustomMembership
          title={title}
          description={description}
          options={fieldOptions}
        />
      ) : isHomeFeatured ? (
        <>
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field data={description} className={defaultClasses.description} options={fieldOptions} />
              <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
            </header>
          ) : null}
          <CustomFeatured />
        </>
      ) : (
        <>
          {hasHeaderFields ? (
            <header className={defaultClasses.sectionDetails}>
              <Field data={title} className={defaultClasses.title} options={fieldOptions} />
              <Field data={description} className={defaultClasses.description} options={fieldOptions} />
              <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
            </header>
          ) : null}
          {hasBlocks ? (
            <div
              className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
                [css.noSidePaddings]: isInsideContainer,
              })}
            >
              <BlockBuilder
                ctaButtonClass={defaultClasses.ctaButton}
                blocks={blocks}
                sectionId={sectionId}
                responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                options={options}
              />
            </div>
          ) : null}
        </>
      )}
    </SectionContainer>
  );
};

export default SectionColumns;
